import { useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { PhotoSlot } from "@shyne/shared/api";
import { AppointmentWorkflow } from "@/models/appointment-task.model";
import { AppointmentClockData } from "@/models/appointment.model";
import { optimizeImage, formatFileSize } from "@/lib/image-optimizer";
import { getDistinctWorkflowDescription } from "@/lib/utils/workflow-photo-gate";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Upload,
  CheckCircle2,
  Loader2,
  X,
  Trash2,
  Plus,
} from "lucide-react";

interface BulkUploadFile {
  id: string;
  file: File;
  previewUrl: string;
  assignedStepKey: string | null;
}

type StepType = "clock" | "workflow";

type UploadStep = {
  key: string;
  label: string;
  description: string | null;
  stepType: StepType;
  workflowId?: string;
  isAfter?: boolean;
  clockType?: "clockin" | "clockout";
};

interface BulkPhotoUploadDialogProps {
  open: boolean;
  onClose: () => void;
  serviceWorkflows: PhotoSlot[];
  appointmentWorkflows: AppointmentWorkflow[];
  clockInData?: AppointmentClockData;
  clockOutData?: AppointmentClockData;
  onPhotoUpload: (workflowId: string, file: File, isAfter: boolean) => Promise<void>;
  onClockUpload: (type: "clockin" | "clockout", file: File) => Promise<void>;
}

export function BulkPhotoUploadDialog({
  open,
  onClose,
  serviceWorkflows,
  appointmentWorkflows,
  clockInData,
  clockOutData,
  onPhotoUpload,
  onClockUpload,
}: BulkPhotoUploadDialogProps) {
  const [bulkFiles, setBulkFiles] = useState<BulkUploadFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const steps: UploadStep[] = [];

  // Clock-in step
  steps.push({
    key: "clockin-photo",
    label: "Start Work Photo",
    description: "Photo to confirm arrival",
    stepType: "clock",
    clockType: "clockin",
  });

  // Workflow before/after steps
  serviceWorkflows.forEach((wf) => {
    const description = getDistinctWorkflowDescription(wf);
    steps.push({
      key: `before-${wf.id}`,
      label: `${wf.title} - Before`,
      description,
      stepType: "workflow",
      workflowId: wf.id,
      isAfter: false,
    });
    steps.push({
      key: `after-${wf.id}`,
      label: `${wf.title} - After`,
      description,
      stepType: "workflow",
      workflowId: wf.id,
      isAfter: true,
    });
  });

  // Clock-out step
  steps.push({
    key: "clockout-photo",
    label: "Complete Work Photo",
    description: "Photo to confirm departure",
    stepType: "clock",
    clockType: "clockout",
  });

  // Determine which steps already have photos
  const alreadyDone = new Set<string>();
  if (clockInData?.photoUrl) alreadyDone.add("clockin-photo");
  if (clockOutData?.photoUrl) alreadyDone.add("clockout-photo");
  serviceWorkflows.forEach((wf) => {
    const hasBefore = appointmentWorkflows.some((aw) => aw.workflow === wf.id && !aw.is_after);
    const hasAfter = appointmentWorkflows.some((aw) => aw.workflow === wf.id && aw.is_after);
    if (hasBefore) alreadyDone.add(`before-${wf.id}`);
    if (hasAfter) alreadyDone.add(`after-${wf.id}`);
  });

  const incompleteSteps = steps.filter((s) => !alreadyDone.has(s.key));

  const handleFilesSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      const newEntries: BulkUploadFile[] = Array.from(files)
        .filter((f) => f.type.startsWith("image/"))
        .map((f) => ({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          file: f,
          previewUrl: URL.createObjectURL(f),
          assignedStepKey: null,
        }));

      if (newEntries.length === 0) {
        toast.error("No valid image files selected");
        e.target.value = "";
        return;
      }

      setBulkFiles((prev) => [...prev, ...newEntries]);
      e.target.value = "";
    },
    []
  );

  const removeBulkFile = (id: string) => {
    setBulkFiles((prev) => {
      const entry = prev.find((f) => f.id === id);
      if (entry) URL.revokeObjectURL(entry.previewUrl);
      return prev.filter((f) => f.id !== id);
    });
  };

  const assignBulkFile = (id: string, stepKey: string) => {
    setBulkFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, assignedStepKey: stepKey } : f))
    );
  };

  const assignedFiles = bulkFiles.filter((f) => f.assignedStepKey !== null);
  const canSubmit = assignedFiles.length > 0;

  const handleSubmit = async () => {
    const toUpload = bulkFiles.filter((f) => f.assignedStepKey !== null);
    if (toUpload.length === 0) return;

    setSubmitting(true);
    setUploadProgress(0);
    let uploaded = 0;

    try {
      for (const entry of toUpload) {
        const step = steps.find((s) => s.key === entry.assignedStepKey);
        if (!step) continue;

        const optimized = await optimizeImage(entry.file);
        const originalSize = formatFileSize(entry.file.size);
        const optimizedSize = formatFileSize(optimized.size);
        if (optimized.size < entry.file.size * 0.8) {
          toast.info(`${step.label}: ${originalSize} → ${optimizedSize}`);
        }

        if (step.stepType === "clock" && step.clockType) {
          await onClockUpload(step.clockType, optimized);
        } else if (step.stepType === "workflow" && step.workflowId !== undefined && step.isAfter !== undefined) {
          await onPhotoUpload(step.workflowId, optimized, step.isAfter);
        }

        uploaded++;
        setUploadProgress(Math.round((uploaded / toUpload.length) * 100));
      }

      // Clean up preview URLs
      bulkFiles.forEach((f) => URL.revokeObjectURL(f.previewUrl));
      setBulkFiles([]);

      toast.success(`${uploaded} photo${uploaded !== 1 ? "s" : ""} uploaded successfully`);
      onClose();
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Upload failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseDialog = () => {
    if (submitting) return;
    bulkFiles.forEach((f) => URL.revokeObjectURL(f.previewUrl));
    setBulkFiles([]);
    onClose();
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div
        className={cn(
          "flex flex-col bg-background w-full max-w-lg rounded-xl border shadow-2xl overflow-hidden",
          "max-h-[90vh]"
        )}
      >
        {/* Header */}
        <header className="shrink-0 px-4 py-3 flex items-center justify-between border-b">
          <div>
            <h2 className="text-base font-semibold">Upload Photos</h2>
            <p className="text-xs text-muted-foreground">
              Select photos and assign them to workflow steps
            </p>
          </div>
          <button
            onClick={handleCloseDialog}
            disabled={submitting}
            className="min-w-[40px] min-h-[40px] flex items-center justify-center rounded-full hover:bg-muted transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* Body */}
        <main className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
          {/* Add photos button */}
          <Button
            variant="outline"
            className="w-full border-dashed border-2"
            onClick={() => fileInputRef.current?.click()}
            disabled={submitting}
          >
            <Plus className="w-4 h-4 mr-2" />
            {bulkFiles.length === 0 ? "Select Photos" : "Add More Photos"}
          </Button>

          {bulkFiles.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Upload className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No photos selected yet.</p>
              <p className="text-xs mt-1">Click above to choose photos from your device.</p>
            </div>
          )}

          {/* File list with assignment dropdowns */}
          {bulkFiles.map((entry) => (
            <div
              key={entry.id}
              className="flex items-start gap-3 p-2 border rounded-lg"
            >
              <img
                src={entry.previewUrl}
                alt="Preview"
                className="w-14 h-14 object-cover rounded shrink-0"
              />
              <div className="flex-1 min-w-0 space-y-1">
                <p className="text-xs text-muted-foreground truncate">
                  {entry.file.name}
                </p>
                <Select
                  value={entry.assignedStepKey || ""}
                  onValueChange={(val) => assignBulkFile(entry.id, val)}
                  disabled={submitting}
                >
                  <SelectTrigger className="w-full h-8 text-xs">
                    <SelectValue placeholder="Assign to step..." />
                  </SelectTrigger>
                  <SelectContent>
                    {incompleteSteps.map((step) => {
                      const alreadyAssigned = bulkFiles.some(
                        (f) =>
                          f.id !== entry.id &&
                          f.assignedStepKey === step.key
                      );
                      return (
                        <SelectItem
                          key={step.key}
                          value={step.key}
                          disabled={alreadyAssigned}
                        >
                          {step.label}
                          {alreadyAssigned ? " (assigned)" : ""}
                        </SelectItem>
                      );
                    })}
                    {/* Also show already-done steps for re-upload */}
                    {steps
                      .filter((s) => alreadyDone.has(s.key))
                      .map((step) => {
                        const alreadyAssigned = bulkFiles.some(
                          (f) =>
                            f.id !== entry.id &&
                            f.assignedStepKey === step.key
                        );
                        return (
                          <SelectItem
                            key={step.key}
                            value={step.key}
                            disabled={alreadyAssigned}
                          >
                            {step.label} (replace)
                            {alreadyAssigned ? " (assigned)" : ""}
                          </SelectItem>
                        );
                      })}
                  </SelectContent>
                </Select>
              </div>
              <button
                onClick={() => removeBulkFile(entry.id)}
                disabled={submitting}
                className="shrink-0 min-w-[40px] min-h-[40px] flex items-center justify-center"
              >
                <Trash2 className="w-4 h-4 text-destructive" />
              </button>
            </div>
          ))}

          {/* Still need list */}
          {bulkFiles.length > 0 && incompleteSteps.length > 0 && (
            <div className="text-xs text-muted-foreground mt-2">
              <p className="font-medium mb-1">Still need photos for:</p>
              <ul className="list-disc pl-4 space-y-0.5">
                {incompleteSteps
                  .filter(
                    (step) =>
                      !bulkFiles.some((f) => f.assignedStepKey === step.key)
                  )
                  .map((step) => (
                    <li key={step.key}>{step.label}</li>
                  ))}
              </ul>
            </div>
          )}
        </main>

        {/* Footer */}
        <footer className="shrink-0 px-4 py-3 border-t bg-background space-y-2">
          {submitting && (
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCloseDialog}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              className="ml-auto"
              disabled={!canSubmit || submitting}
              onClick={handleSubmit}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading... {uploadProgress}%
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Upload {assignedFiles.length > 0 ? `(${assignedFiles.length})` : ""}
                </>
              )}
            </Button>
          </div>
        </footer>
      </div>

      {/* Hidden file input */}
      <input
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        ref={fileInputRef}
        onChange={handleFilesSelect}
      />
    </div>,
    document.body
  );
}
